'use client';
import { useQuery } from '@tanstack/react-query';

import { ClobBook } from '@/types/clob.types';
import { clobDataApi } from '../api/clobData.api';

export const CLOB_DATA_QUERY_KEYS = {
  all: ['clob-data'] as const,
  books: (tokenIds: string[]) => [...CLOB_DATA_QUERY_KEYS.all, 'books', ...tokenIds] as const,
  orders: (marketId: string, user: string) =>
    [...CLOB_DATA_QUERY_KEYS.all, 'orders', marketId, user] as const,
  trades: (marketId: string, user: string) =>
    [...CLOB_DATA_QUERY_KEYS.all, 'trades', marketId, user] as const,
  pricesHistory: (marketId: string, interval: string) =>
    [...CLOB_DATA_QUERY_KEYS.all, 'prices-history', marketId, interval] as const,
};

/**
 * Hook to fetch price history for a specific market asset.
 *
 * @param marketId - The CLOB token ID
 * @param interval - The time interval (e.g., '1d')
 * @param enabled - Whether the query should be enabled
 * @returns Query object with history data
 */
export const useClobPricesHistory = (marketId: string, interval = '1d', enabled = true) => {
  return useQuery({
    queryKey: CLOB_DATA_QUERY_KEYS.pricesHistory(marketId, interval),
    queryFn: () => clobDataApi.getPricesHistory(marketId, interval),
    enabled: enabled && !!marketId,
    staleTime: 60 * 1000, // 1 minute stale time for history
  });
};

/**
 * Hook to fetch batch order books for a list of token IDs.
 *
 * @param tokenIds - Array of token_id strings
 * @param enabled - Whether the query should be enabled
 * @returns Query object with books data
 */
export const useClobBooks = (tokenIds: string[], enabled = true) => {
  return useQuery<ClobBook[]>({
    queryKey: CLOB_DATA_QUERY_KEYS.books(tokenIds),
    queryFn: () => clobDataApi.getBooks(tokenIds),
    enabled: enabled && tokenIds.length > 0,
    staleTime: 10 * 1000, // 10 seconds state time for real-time books
    refetchInterval: 15 * 1000, // Poll every 15 seconds
  });
};

/**
 * Hook to fetch user orders for a specific market.
 *
 * @param marketId - The market ID
 * @param user - User address
 * @param clobClient - ClobClient for signing
 * @param enabled - Whether the query should be enabled
 */
export const useClobOrders = (
  marketId: string,
  user?: string,
  clobClient?: any,
  enabled = true,
) => {
  return useQuery<any>({
    queryKey: CLOB_DATA_QUERY_KEYS.orders(marketId, user || ''),
    queryFn: async () => {
      // @polymarket/client SecureClient: listOpenOrders returns a paginator whose
      // items are OpenOrder[] (same snake_case fields as the old /data/orders REST
      // response). Auth is handled internally, so no manual header signing.
      const page = await clobClient.listOpenOrders({ market: marketId }).firstPage();
      return page.items;
    },
    enabled: enabled && !!marketId && !!clobClient,
    refetchInterval: 10000,
  });
};

/**
 * Hook to fetch user trades for a specific market.
 *
 * @param marketId - The market ID
 * @param user - User address
 * @param clobClient - ClobClient for signing
 * @param enabled - Whether the query should be enabled
 */
export const useClobTrades = (
  marketId: string,
  user?: string,
  clobClient?: any,
  enabled = true,
) => {
  return useQuery<any>({
    queryKey: CLOB_DATA_QUERY_KEYS.trades(marketId, user || ''),
    queryFn: async () => {
      // SecureClient listAccountTrades paginator; items are ClobTrade[]. Auth is
      // handled internally by the SDK (no manual header signing).
      const page = await clobClient.listAccountTrades({ market: marketId }).firstPage();
      return page.items;
    },
    enabled: enabled && !!marketId && !!clobClient,
    refetchInterval: 30000,
  });
};
